import { createTheme, PaletteOptions } from '@mui/material';
import { Components } from '@mui/material/styles/components';
import { Theme } from '@mui/material/styles/createTheme';
import { colors } from './colors';
import { gradients } from './gradients';

import { fonts } from './ui-version';

/** Buttons, tabs, links and breadcrumbs share one text style so every action reads the same. */
const actionTextStyle = {
  fontFamily: fonts.heading,
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '1px',
  textTransform: 'uppercase' as const,
};

const headingStyle = {
  fontFamily: fonts.heading,
  fontWeight: 500,
};

const palette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: colors.secondary[50],
    light: colors.secondary[20],
    dark: colors.secondary[70],
    contrastText: colors.text.main,
  },
  secondary: {
    main: colors.primary[60],
    light: colors.primary[30],
    dark: colors.primary[80],
    contrastText: colors.text.main,
  },
  error: {
    main: colors.critical[50],
    dark: colors.critical[60],
    light: colors.critical[20],
  },
  success: {
    main: colors.success[60],
    dark: colors.success[70],
    light: colors.success[40],
  },
  warning: {
    main: colors.warning[60],
    light: colors.warning[50],
    dark: colors.warning[70],
    contrastText: colors.primary[0],
  },
  info: {
    main: colors.tertiary[80],
    dark: colors.tertiary[90],
    light: colors.tertiary[50],
  },
  text: {
    primary: colors.text.main,
    secondary: colors.text.secondary,
    disabled: colors.text.disabled,
  },
  action: {
    hover: colors.surface.hover,
    disabled: colors.text.disabled,
    disabledBackground: colors.surface.disabled,
  },
  divider: colors.border.default,
  background: {
    default: colors.surface.app,
    paper: colors.surface.card,
  },
};

const floatingPanelBorder = { border: `1px solid ${colors.border.default}` };

const components: Components<Omit<Theme, 'components'>> = {
  MuiAutocomplete: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      clearIndicator: { color: colors.icon.secondary, '&:hover': { color: colors.icon.main } },
      popupIndicator: { color: colors.icon.secondary, '&:hover': { color: colors.icon.main } },
      // No shadows: a panel floating over the page gets a hairline edge instead
      paper: floatingPanelBorder,
      // The first and last rows sit flush with the panel's edge; MUI's 8px would show as a white strip
      listbox: { padding: 0 },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: floatingPanelBorder,
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      li: { '& > *': actionTextStyle },
    },
  },
  // Colour only: MuiLink also wraps data (websites, names in cells), which keeps its own text style
  MuiLink: {
    styleOverrides: {
      root: { color: colors.text.link },
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: {
        textTransform: 'uppercase',
        fontSize: '12px',
      },
    },
    variants: [
      {
        props: { variant: 'outlined', color: 'primary' },
        style: {
          color: colors.text.main,
          borderColor: colors.text.main,
          '&:hover': { backgroundColor: colors.primary[10] },
        },
      },
      {
        props: { variant: 'outlined', color: 'secondary' },
        style: {
          color: colors.text.main,
          borderColor: colors.text.main,
          '&:hover': { backgroundColor: colors.primary[10] },
        },
      },
      {
        props: { variant: 'text', color: 'primary' },
        style: { color: colors.text.link, '&:hover': { backgroundColor: colors.primary[10] } },
      },
      {
        props: { variant: 'text', color: 'secondary' },
        style: { color: colors.text.link, '&:hover': { backgroundColor: colors.primary[10] } },
      },
    ],
  },
  MuiChip: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: { borderRadius: '6px' },
    },
  },
  MuiFab: {
    defaultProps: {
      size: 'small',
    },
  },
  MuiFilledInput: {
    defaultProps: {
      size: 'small',
      margin: 'dense',
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: 'small',
      margin: 'dense',
    },
  },
  MuiFormHelperText: {
    defaultProps: {
      margin: 'dense',
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'small',
    },
    variants: [
      // A main action is dark; a secondary one (the same icon on every row, a "more" menu) is grey until hovered
      {
        props: { color: 'default' },
        style: { color: colors.icon.secondary, '&:hover': { color: colors.icon.main } },
      },
      { props: { color: 'primary' }, style: { color: colors.icon.main } },
      { props: { color: 'secondary' }, style: { color: colors.icon.main } },
      // 20px icons in a button that keeps its size, so the click area is unchanged
      { props: { size: 'small' }, style: { '& .MuiSvgIcon-root': { fontSize: '20px' } } },
      { props: { size: 'medium' }, style: { '& .MuiSvgIcon-root': { fontSize: '20px' } } },
    ],
    styleOverrides: {
      root: { borderRadius: '6px', '&:hover': { backgroundColor: colors.primary[10] } },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        '& .MuiIconButton-root': { color: colors.icon.secondary },
        '& .MuiIconButton-root:hover': { color: colors.icon.main },
      },
    },
  },
  MuiInputBase: {
    defaultProps: {
      size: 'small',
      margin: 'dense',
    },
  },
  MuiInputLabel: {
    defaultProps: {
      size: 'small',
      margin: 'dense',
    },
  },
  MuiListItem: {
    defaultProps: {
      dense: true,
    },
  },
  MuiMenuItem: {
    defaultProps: {
      dense: true,
    },
  },
  MuiOutlinedInput: {
    defaultProps: {
      size: 'small',
      margin: 'dense',
    },
    styleOverrides: {
      notchedOutline: { borderColor: colors.border.input },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      // Without a shadow the white thumb needs an edge while off; on, it is purple and needs none
      thumb: { boxShadow: `0 0 0 1px ${colors.border.input}` },
      switchBase: { '&.Mui-checked .MuiSwitch-thumb': { boxShadow: 'none' } },
      track: { backgroundColor: colors.neutral[40], opacity: 1 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'uppercase',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '.MuiTabs-indicator': {
          backgroundColor: colors.border.tabIndicator,
        },
        '.MuiTab-root': {
          fontWeight: 600,
          color: colors.text.tab,
        },
        '.MuiTab-root.Mui-selected': {
          fontWeight: 600,
          color: colors.text.tabSelected,
        },
      },
    },
  },
  MuiTable: {
    defaultProps: {
      size: 'small',
    },
  },
  MuiTextField: {
    defaultProps: {
      size: 'small',
      margin: 'dense',
    },
    styleOverrides: {
      root: {
        '&&& .MuiInputBase-input': {
          fontFamily: fonts.base,
          fontSize: '14px',
        },
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderColor: colors.secondary[60],
          },
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'uppercase',
        fontSize: '12px',
        color: colors.text.secondary,
        borderColor: colors.border.input,
        '&.Mui-selected': { backgroundColor: colors.primary[10], color: colors.text.main },
        '&.Mui-selected:hover': { backgroundColor: colors.primary[20] },
      },
    },
  },
  MuiToggleButtonGroup: {
    defaultProps: {
      size: 'small',
    },
  },
  MuiToolbar: {
    defaultProps: {
      variant: 'dense',
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        color: colors.text.main,
        backgroundColor: colors.primary[0],
        boxShadow: 'none',
        border: `thin solid ${colors.border.default}`,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      standardInfo: {
        backgroundColor: colors.tertiary[20],
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        caption2: 'span',
        monotype: 'span',
        tpgrOverline: 'span',
      },
    },
  },
};

export const CustomTypographyVariants = {
  h1: {
    ...headingStyle,
    fontSize: '40px',
    lineHeight: '116.7%',
  },
  h2: {
    ...headingStyle,
    fontSize: '36px',
    lineHeight: '116.5%',
  },
  h3: {
    ...headingStyle,
    fontSize: '32px',
    lineHeight: '123.4%',
  },
  h4: {
    ...headingStyle,
    fontSize: '28px',
    lineHeight: '123.5%',
  },
  h5: {
    ...headingStyle,
    fontSize: '24px',
    lineHeight: '133.4%',
  },
  h6: {
    ...headingStyle,
    fontSize: '20px',
    lineHeight: '160%',
  },
  subtitle1: headingStyle,
  subtitle2: headingStyle,
  body1: {
    fontSize: '14px',
  },
  body2: {
    fontSize: '13px',
  },
  button: actionTextStyle,
  caption2: {
    fontSize: '10px',
    lineHeight: '166%',
  },
  monotype: {
    fontFamily: fonts.base,
    fontSize: '14px',
    lineHeight: '144%',
  },
  tpgrOverline: {
    fontSize: '12px',
    lineHeight: '266%',
    textTransform: 'uppercase' as const,
  },
};

export const newUiThemeOptions: Parameters<typeof createTheme>[0] = {
  cssVariables: true,
  palette,
  gradients,
  spacing: 2,
  shape: { borderRadius: 6 },
  // The New UI is flat: surfaces are told apart by colour and hairlines, never by elevation
  shadows: Array(25).fill('none') as Theme['shadows'],
  typography: {
    fontFamily: fonts.base,
    fontWeightBold: 700,
    fontWeightLight: 300,
    ...CustomTypographyVariants,
  },
  components,
  colors,
};
